import React, { useEffect, useMemo } from 'react';
import {
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
  CButton,
  CInputGroup,
  CInput,
  CInputGroupAppend,
  CAlert,
  CSpinner,
  CTooltip,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import _t from 'counterpart';
import { useMutation } from 'react-query';
import {
  generateDepositLink,
  sendDepositLinkViaSms,
} from '../../../services/BackendService';
import { extractErrorMessage } from '../../../helpers';
import ButtonWithLoader from '../../../components/ButtonWithLoader';
import toast from 'react-hot-toast';

interface IProps {
  show: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  canSendSms: boolean;
  canSendWhatsApp: boolean;
  twilioConfigured: boolean;
  customerHasPhone: boolean;
}

const GenerateDepositLinkModal = ({
  show,
  onClose,
  customerId,
  customerName,
  canSendSms,
  canSendWhatsApp,
  twilioConfigured,
  customerHasPhone,
}: IProps) => {
  const generateLinkMutation = useMutation(() => generateDepositLink(customerId), {
    onError: (e) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const sendDepositLinkViaSmsMutation = useMutation(() => sendDepositLinkViaSms(customerId), {
    onSuccess: () => {
      toast.success(_t('customer.deposit-link-sent-via-sms'));
      onClose();
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const depositUrl = generateLinkMutation.data?.depositUrl ?? null;
  const linkGeneratedWithoutView =
    generateLinkMutation.isSuccess && !generateLinkMutation.isLoading && depositUrl === null;

  useEffect(() => {
    if (show && customerId && !depositUrl && !generateLinkMutation.isLoading && !generateLinkMutation.isError) {
      generateLinkMutation.mutate();
    }
  }, [show, customerId]);

  const handleCopy = async () => {
    if (depositUrl) {
      await navigator.clipboard.writeText(depositUrl);
      toast.success(_t('customer.deposit-link-copied'));
    }
  };

  const handleSendSms = () => {
    if (canSendSms) {
      sendDepositLinkViaSmsMutation.mutate();
    }
  };

  const handleClose = () => {
    generateLinkMutation.reset();
    sendDepositLinkViaSmsMutation.reset();
    onClose();
  };

  const isSending = sendDepositLinkViaSmsMutation.isLoading;
  const smsDisabled = isSending || !canSendSms;

  const smsTooltip = useMemo(() => {
    if (!twilioConfigured) return _t('customer.sms-twilio-not-configured');
    if (!customerHasPhone) return _t('customer.sms-no-phone-number');
    return '';
  }, [twilioConfigured, customerHasPhone]);

  const footerButtonStyle: React.CSSProperties = {
    minHeight: 42,
    paddingTop: 10,
    paddingBottom: 10,
    lineHeight: 1.35,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const smsButton = (
    <ButtonWithLoader
      isLoading={sendDepositLinkViaSmsMutation.isLoading}
      onClick={handleSendSms}
      buttonColor="info"
      spinnerColor="secondary"
      title={_t('customer.send-via-sms')}
      disabled={smsDisabled}
      style={footerButtonStyle}
    />
  );

  return (
    <CModal show={show} onClose={handleClose}>
      <CModalHeader closeButton>
        <CModalTitle>{_t('customer.generate-deposit-link-for', { customer: customerName })}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {generateLinkMutation.isError && (
          <CAlert color="danger">{extractErrorMessage(generateLinkMutation.error)}</CAlert>
        )}
        {generateLinkMutation.isLoading && !depositUrl ? (
          <div className="d-flex align-items-center gap-2">
            <CSpinner size="sm" />
            <span>{_t('customer.generate-deposit-link-description')}</span>
          </div>
        ) : depositUrl ? (
          <>
            <p className="text-muted small mb-2">{_t('customer.deposit-link-validity')}</p>
            <CInputGroup>
              <CInput type="text" value={depositUrl} readOnly />
              <CInputGroupAppend>
                <CButton color="primary" onClick={handleCopy}>
                  <CIcon name="cilClipboard" />
                </CButton>
              </CInputGroupAppend>
            </CInputGroup>
          </>
        ) : linkGeneratedWithoutView ? (
          <>
            <p
              className="text-success text-center mb-0"
              style={{ fontSize: '1.125rem' }}
            >
              {_t('customer.deposit-link-generated-success')}
            </p>
            <p className="text-muted small text-center mb-0 mt-2">
              {_t('customer.deposit-link-validity')}
            </p>
          </>
        ) : null}
      </CModalBody>
      <CModalFooter
        className="flex-wrap gap-2 align-items-center"
        style={{ paddingTop: 16, paddingBottom: 16, minHeight: 72 }}
      >
        {!depositUrl && !linkGeneratedWithoutView ? (
          <CButton color="light" variant="outline" onClick={handleClose} style={footerButtonStyle}>
            {_t('global.cancel')}
          </CButton>
        ) : (
          <>
            {smsTooltip ? (
              <CTooltip content={smsTooltip}>
                <span style={{ display: 'inline-block' }}>{smsButton}</span>
              </CTooltip>
            ) : (
              smsButton
            )}
            <CButton
              color="light"
              variant="outline"
              onClick={handleClose}
              disabled={isSending}
              style={footerButtonStyle}
            >
              {_t('global.close')}
            </CButton>
          </>
        )}
      </CModalFooter>
    </CModal>
  );
};

export default React.memo(GenerateDepositLinkModal);
